import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import { TeamPeriodSelect } from '../components/team/TeamPeriodSelect';
import type { TeamMemberStats, TeamStatsResponse } from '../lib/teamStats';
import {
  formatGoalScore,
  formatPracticeDuration,
  formatReachRate,
  memberReachRate,
  parseTeamPeriod,
  type TeamPeriod,
} from '../lib/teamPeriod';

type SortKey = 'name' | 'sessions' | 'avg' | 'reach' | 'last' | 'time';

function formatLastPracticed(iso: string | null): string {
  if (!iso) return 'Never';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Never';
  return d.toLocaleDateString();
}

function scoreClass(score: number | null): string {
  if (score == null) return 'text-gray-400';
  return score >= 7 ? 'text-green-700 font-medium' : 'text-amber-700 font-medium';
}

export function TeamDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parseTeamPeriod(searchParams.get('period'));
  const [stats, setStats] = useState<TeamStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('avg');
  const [sortDir, setSortDir] = useState<1 | -1>(-1);

  const setPeriod = useCallback(
    (next: TeamPeriod) => {
      setSearchParams(next === '30d' ? {} : { period: next }, { replace: true });
    },
    [setSearchParams],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<TeamStatsResponse>(`/practice/team/stats?period=${period}`)
      .then((res) => {
        if (!cancelled) setStats(res.data);
      })
      .catch(() => {
        if (!cancelled) {
          setStats(null);
          setError('Could not load team performance.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === -1 ? 1 : -1));
    } else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'last' ? 1 : -1);
    }
  };

  const members = useMemo(() => {
    if (!stats) return [];
    return stats.members.slice().sort((a, b) => {
      const nameA = (a.name || a.email).toLowerCase();
      const nameB = (b.name || b.email).toLowerCase();
      let av: number | string | null;
      let bv: number | string | null;
      if (sortKey === 'name') {
        av = nameA;
        bv = nameB;
      } else if (sortKey === 'sessions') {
        av = a.sessionCount;
        bv = b.sessionCount;
      } else if (sortKey === 'avg') {
        av = a.avgGoalScore;
        bv = b.avgGoalScore;
      } else if (sortKey === 'reach') {
        av = memberReachRate(a.goalReachedCount, a.sessionCount);
        bv = memberReachRate(b.goalReachedCount, b.sessionCount);
      } else if (sortKey === 'last') {
        av = a.lastPracticedAt;
        bv = b.lastPracticedAt;
      } else {
        av = a.totalPracticeSeconds;
        bv = b.totalPracticeSeconds;
      }
      if (av == null && bv == null) return nameA.localeCompare(nameB);
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av === bv) return nameA.localeCompare(nameB);
      if (typeof av === 'string' && typeof bv === 'string') {
        return av.localeCompare(bv) * sortDir;
      }
      return (av > bv ? 1 : -1) * sortDir;
    });
  }, [stats, sortKey, sortDir]);

  const periodQuery = period === '30d' ? '' : `?period=${period}`;

  return (
    <div>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0" data-tour="team-intro">
          <h1 className="text-2xl font-bold text-gray-900">Team</h1>
          <p className="text-gray-600 mt-1">Practice performance across your organization.</p>
        </div>
        <TeamPeriodSelect value={period} onChange={setPeriod} />
      </div>

      {loading && <p className="text-gray-500">Loading...</p>}
      {error && (
        <p className="text-red-600" data-tour="team-error">
          {error}
        </p>
      )}

      {stats && (
        <>
          <div
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8"
            data-tour="team-kpis"
          >
            <Kpi value={String(stats.org.totalSessions)} label="Sessions" />
            <Kpi value={String(stats.org.completedSessions)} label="Completed" />
            <Kpi value={formatGoalScore(stats.org.avgGoalScore)} label="Avg goal score" />
            <Kpi value={formatReachRate(stats.org.goalReachRate)} label="Goal reached" />
            <Kpi
              value={formatPracticeDuration(stats.org.totalPracticeSeconds)}
              label="Practice time"
            />
          </div>

          <div className="bg-white rounded-lg border overflow-hidden" data-tour="team-members">
            <div className="p-4 border-b">
              <h2 className="font-semibold text-gray-900">Members</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <SortHeader
                      label="Name"
                      active={sortKey === 'name'}
                      onClick={() => toggleSort('name')}
                    />
                    <th className="px-4 py-3 font-medium">Role</th>
                    <SortHeader
                      label="Sessions"
                      active={sortKey === 'sessions'}
                      align="right"
                      onClick={() => toggleSort('sessions')}
                    />
                    <SortHeader
                      label="Avg score"
                      active={sortKey === 'avg'}
                      align="right"
                      onClick={() => toggleSort('avg')}
                    />
                    <SortHeader
                      label="Reach"
                      active={sortKey === 'reach'}
                      align="right"
                      onClick={() => toggleSort('reach')}
                    />
                    <SortHeader
                      label="Last practiced"
                      active={sortKey === 'last'}
                      onClick={() => toggleSort('last')}
                    />
                    <SortHeader
                      label="Time"
                      active={sortKey === 'time'}
                      align="right"
                      onClick={() => toggleSort('time')}
                    />
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => (
                    <MemberRow key={m.userId} member={m} periodQuery={periodQuery} />
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ value, label }: { value: string; label: string }) {
  return (
    <div className="bg-white p-5 rounded-lg border shadow-sm">
      <div className="text-3xl font-bold text-green-700">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}

function SortHeader({
  label,
  active,
  align,
  onClick,
}: {
  label: string;
  active: boolean;
  align?: 'right';
  onClick: () => void;
}) {
  return (
    <th className={`px-4 py-3 font-medium ${align === 'right' ? 'text-right' : ''}`}>
      <button
        type="button"
        onClick={onClick}
        aria-pressed={active}
        className={`hover:text-gray-900 ${active ? 'text-gray-900' : ''}`}
      >
        {label}
      </button>
    </th>
  );
}

function MemberRow({ member, periodQuery }: { member: TeamMemberStats; periodQuery: string }) {
  const reach = memberReachRate(member.goalReachedCount, member.sessionCount);
  return (
    <tr className="border-b last:border-b-0 hover:bg-gray-50">
      <td className="px-4 py-3">
        <Link
          to={`/team/${member.userId}${periodQuery}`}
          className="font-medium text-gray-900 hover:text-green-800"
        >
          {member.name || member.email}
        </Link>
        {member.name && <div className="text-xs text-gray-500 mt-0.5">{member.email}</div>}
      </td>
      <td className="px-4 py-3">
        <span className="text-xs px-2 py-1 bg-gray-100 rounded font-medium">{member.role}</span>
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{member.sessionCount}</td>
      <td className={`px-4 py-3 text-right tabular-nums ${scoreClass(member.avgGoalScore)}`}>
        {member.avgGoalScore == null ? '—' : `${formatGoalScore(member.avgGoalScore)}/10`}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">{formatReachRate(reach)}</td>
      <td className={`px-4 py-3 ${member.lastPracticedAt ? '' : 'text-gray-400'}`}>
        {formatLastPracticed(member.lastPracticedAt)}
      </td>
      <td className="px-4 py-3 text-right tabular-nums">
        {formatPracticeDuration(member.totalPracticeSeconds)}
      </td>
    </tr>
  );
}
